import {
  ArrowClockwise,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  CloudArrowUp,
  DownloadSimple,
  FileText,
  Info,
  Sparkle,
  SpinnerGap,
  WarningCircle,
  X,
} from '@phosphor-icons/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui-kit/button';
import { Checkbox } from '@/components/ui-kit/checkbox';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui-kit/table';
import { cn } from '@/lib/utils';
import { ImportContactsStatusBadge } from '../components/import-contacts-status-badge/import-contacts-status-badge';
import { ImportContactsStepIndicator } from '../components/import-contacts-step-indicator/import-contacts-step-indicator';
import {
  useContactImportFields,
  useContactImportJob,
  usePreviewContactImport,
  useStartContactImport,
} from '../hooks/use-contact-import';
import { useGetLists } from '@/modules/lists/hooks/use-lists';
import {
  downloadContactImportErrors,
  getContactImportErrorMessage,
} from '../services/contact-import.service';
import { useCustomFieldDefinitions } from '../hooks/use-custom-field-definitions';
import { getEditableCustomFieldDefinitions } from '../utils/custom-field-attributes.utils';
import type { ContactImportFieldApi } from '../types/contact-import-api.types';
import {
  applyContactImportServerPreview,
  buildContactImportPreviewRows,
  buildInitialContactImportMappingFromFields,
  CONTACT_IMPORT_MAX_FILE_SIZE_BYTES,
  CONTACT_IMPORT_SAMPLE_CSV,
  invertContactImportMapping,
  parseContactCsvForImport,
  summarizeContactImportPreview,
  validateContactImportMapping,
  type ContactImportMappingTarget,
  type ContactImportPreviewRow,
  type ParsedContactCsv,
} from '../utils/contact-import.utils';

const PREVIEW_ROW_LIMIT = 25;
const SKIP_MAPPING_VALUE = '__skip__';

const FIELD_GROUP_LABELS: Record<string, string> = {
  contact: 'Contact fields',
  subscription: 'Subscriptions',
  attribute: 'Attributes',
};

type ImportPhase = 'progress' | 'failed' | 'result';

type ImportResultSummary = {
  rows: number;
  created: number;
  updated: number;
  failed: number;
  needsConfirmation: number;
  allRowsFailed: boolean;
};

const KpiTile = ({ label, value }: { label: string; value: number }) => (
  <div className="flex flex-col gap-1 rounded-xl border border-neutral-200 bg-white p-4">
    <span className="text-xs text-neutral-600">{label}</span>
    <span className="text-2xl font-semibold text-neutral-900">{value.toLocaleString()}</span>
  </div>
);

const IMPORT_JOB_STAGES = [
  { status: 'queued', label: 'Queued' },
  { status: 'processing', label: 'Processing' },
  { status: 'completed', label: 'Completed' },
] as const;

/** queued → processing → completed, with the job's current stage highlighted. */
const ImportJobStages = ({ status }: { status: string }) => {
  const currentIndex = Math.max(
    0,
    IMPORT_JOB_STAGES.findIndex((stage) => stage.status === status)
  );

  return (
    <ol className="flex w-full items-center justify-center gap-2 text-xs" aria-label="Import stage">
      {IMPORT_JOB_STAGES.map((stage, index) => (
        <li
          key={stage.status}
          className="flex items-center gap-2"
          aria-current={index === currentIndex ? 'step' : undefined}
        >
          {index > 0 ? <span className="h-px w-6 bg-neutral-200" aria-hidden /> : null}
          <span
            className={
              index < currentIndex
                ? 'font-medium text-emerald-700'
                : index === currentIndex
                  ? 'font-semibold text-primary-600'
                  : 'text-neutral-500'
            }
          >
            {stage.label}
          </span>
        </li>
      ))}
    </ol>
  );
};

const isMappingOrFileError = (message: string) => {
  const lower = message.toLowerCase();
  return (
    lower.includes('mapping') ||
    lower.includes('column') ||
    lower.includes('file') ||
    lower.includes('csv')
  );
};

const isFileScopedError = (message: string) => {
  const lower = message.toLowerCase();
  return lower.includes('file') || lower.includes('csv');
};

export const ImportContactsPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: importFieldsData, isError: fieldsError } = useContactImportFields();
  const fields = useMemo(() => importFieldsData?.fields ?? [], [importFieldsData?.fields]);
  const fieldTypeByTarget = useMemo(
    () => new Map<string, string>(fields.map((field) => [field.field, field.type])),
    [fields]
  );
  const { mutateAsync: startImport, isPending: isStartingImport } = useStartContactImport();
  const { mutateAsync: runPreview, isPending: isPreviewing } = usePreviewContactImport();
  const { data: listsData } = useGetLists({
    pageNo: 1,
    pageSize: 200,
  });

  const lists = useMemo(() => listsData?.items ?? [], [listsData?.items]);
  const { data: customFieldDefinitionsData } = useCustomFieldDefinitions();
  const requiredAttributes = useMemo(
    () =>
      getEditableCustomFieldDefinitions(customFieldDefinitionsData?.items ?? [])
        .filter((definition) => definition.Required)
        .map((definition) => ({ key: definition.Key, label: definition.Label })),
    [customFieldDefinitionsData?.items]
  );

  const [currentStep, setCurrentStep] = useState(1);
  const [isDragOver, setIsDragOver] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [mappingBanner, setMappingBanner] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [selectedFileSize, setSelectedFileSize] = useState<number | null>(null);
  const [parsedData, setParsedData] = useState<ParsedContactCsv | null>(null);
  const [columnMapping, setColumnMapping] = useState<Record<string, ContactImportMappingTarget>>(
    {}
  );
  const [selectedListIds, setSelectedListIds] = useState<string[]>([]);
  const [previewRows, setPreviewRows] = useState<ContactImportPreviewRow[]>([]);
  // 'server' once the backend dry run answered; 'local' when it failed and only the browser check ran.
  const [previewSource, setPreviewSource] = useState<'server' | 'local' | null>(null);
  const [previewTruncatedAt, setPreviewTruncatedAt] = useState<number | null>(null);
  const previewRequestRef = useRef(0);
  const [importPhase, setImportPhase] = useState<ImportPhase>('progress');
  const [importResult, setImportResult] = useState<ImportResultSummary | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [pollError, setPollError] = useState<string | null>(null);
  const [unmappedKeys, setUnmappedKeys] = useState<string[]>([]);
  const [isDownloadingErrors, setIsDownloadingErrors] = useState(false);
  const [errorReport, setErrorReport] = useState<ParsedContactCsv | null>(null);
  const lastAutoMappedHeadersKeyRef = useRef<string | null>(null);

  const jobQuery = useContactImportJob(jobId, {
    enabled: currentStep === 4 && Boolean(jobId),
  });

  const fieldsByGroup = useMemo(() => {
    const groups: Record<string, ContactImportFieldApi[]> = {};
    for (const field of fields) {
      const group = field.group || 'other';
      if (!groups[group]) groups[group] = [];
      groups[group].push(field);
    }
    return groups;
  }, [fields]);

  const mappingValidation = useMemo(
    () => validateContactImportMapping(columnMapping),
    [columnMapping]
  );

  const previewCounts = useMemo(() => summarizeContactImportPreview(previewRows), [previewRows]);

  const jobStatus = (jobQuery.data?.status ?? '').toLowerCase();

  const processedRows = useMemo(() => {
    const totals = jobQuery.data?.totals;
    if (!totals) return 0;
    return totals.processed ?? totals.created + totals.updated + totals.failed;
  }, [jobQuery.data?.totals]);

  const progressPercent = useMemo(() => {
    const rows = jobQuery.data?.totals?.rows;
    if (!rows) return jobStatus === 'processing' ? 5 : 0;
    return Math.min(100, Math.round((processedRows / rows) * 100));
  }, [jobQuery.data?.totals?.rows, processedRows, jobStatus]);

  useEffect(() => {
    if (!parsedData || fields.length === 0) return;
    const headersKey = parsedData.headers.join('\0');
    if (lastAutoMappedHeadersKeyRef.current === headersKey) return;
    lastAutoMappedHeadersKeyRef.current = headersKey;
    setColumnMapping(buildInitialContactImportMappingFromFields(parsedData.headers, fields));
  }, [parsedData, fields]);

  useEffect(() => {
    const job = jobQuery.data;
    if (!job) return;
    setPollError(null);
    const status = job.status.toLowerCase();
    if (status === 'completed') {
      setImportResult({
        rows: job.totals?.rows ?? 0,
        created: job.totals?.created ?? 0,
        updated: job.totals?.updated ?? 0,
        failed: job.totals?.failed ?? 0,
        needsConfirmation: job.totals?.needsConfirmation ?? 0,
        // The backend's own verdict, not inferred from the totals here.
        allRowsFailed: job.allRowsFailed === true,
      });
      setImportPhase('result');
      setUnmappedKeys(job.unmappedKeys ?? []);
    } else if (status === 'failed') {
      setImportPhase('failed');
      setUploadError('The import job failed while processing. You can retry with the same file.');
    }
  }, [jobQuery.data]);

  useEffect(() => {
    // Transport/poll errors are recoverable — keep jobId and do not mint a new import.
    if (currentStep !== 4 || !jobId || !jobQuery.isError) return;
    if (jobStatus === 'failed' || jobStatus === 'completed') return;
    setPollError(getContactImportErrorMessage(jobQuery.error));
  }, [currentStep, jobId, jobQuery.isError, jobQuery.error, jobStatus]);

  const resetFileSelection = () => {
    setSelectedFile(null);
    setSelectedFileName(null);
    setSelectedFileSize(null);
    setParsedData(null);
    lastAutoMappedHeadersKeyRef.current = null;
    setFileError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleParsedCsv = useCallback((file: File, text: string) => {
    setFileError(null);
    try {
      const parsed = parseContactCsvForImport(text);
      if (!parsed.headers.length || !parsed.rows.length) {
        setFileError('This file has no data rows to import.');
        resetFileSelection();
        return;
      }
      setSelectedFile(file);
      setParsedData(parsed);
      setSelectedFileName(file.name);
      setSelectedFileSize(file.size);
    } catch (error) {
      setFileError(error instanceof Error ? error.message : 'Could not read that file. Try again.');
      resetFileSelection();
    }
  }, []);

  const loadFile = useCallback(
    async (file: File) => {
      setFileError(null);
      if (!/\.csv$/i.test(file.name)) {
        setFileError('Please choose a .csv file.');
        return;
      }
      if (file.size > CONTACT_IMPORT_MAX_FILE_SIZE_BYTES) {
        setFileError('File is larger than 5 MB.');
        return;
      }
      const text = await file.text();
      handleParsedCsv(file, text);
    },
    [handleParsedCsv]
  );

  const loadSampleFile = useCallback(() => {
    const sample = new File([CONTACT_IMPORT_SAMPLE_CSV], 'sample-contacts.csv', {
      type: 'text/csv',
    });
    void loadFile(sample);
  }, [loadFile]);

  const refreshPreview = useCallback(async () => {
    if (!parsedData) return;
    // The local check renders at once and stays as the fallback if the dry run fails.
    const localRows = buildContactImportPreviewRows({
      rows: parsedData.rows,
      mapping: columnMapping,
      requiredAttributes,
    });
    setPreviewRows(localRows);
    setPreviewSource(null);
    setPreviewTruncatedAt(null);
    if (!selectedFile) return;

    const requestId = ++previewRequestRef.current;
    try {
      const preview = await runPreview({
        file: selectedFile,
        mapping: invertContactImportMapping(columnMapping),
        listIds: selectedListIds,
      });
      if (requestId !== previewRequestRef.current) return;
      setPreviewRows(applyContactImportServerPreview(localRows, preview));
      setPreviewSource('server');
      setPreviewTruncatedAt(preview.truncated ? preview.previewedRows : null);
    } catch {
      if (requestId !== previewRequestRef.current) return;
      setPreviewSource('local');
    }
  }, [columnMapping, parsedData, requiredAttributes, runPreview, selectedFile, selectedListIds]);

  const goToStep = (step: number) => {
    if (step === 3) {
      void refreshPreview();
    }
    setCurrentStep(step);
  };

  const handleStartImport = useCallback(
    async (keyOverride?: string) => {
      if (!selectedFile) return;

      const apiMapping = invertContactImportMapping(columnMapping);
      const key = keyOverride ?? idempotencyKey;
      setUploadError(null);
      setPollError(null);
      setMappingBanner(null);
      setImportResult(null);
      setUnmappedKeys([]);
      setImportPhase('progress');
      setCurrentStep(4);

      try {
        const job = await startImport({
          file: selectedFile,
          mapping: apiMapping,
          listIds: selectedListIds,
          idempotencyKey: key,
        });
        setJobId(job.jobId);
      } catch (error) {
        const message = getContactImportErrorMessage(error);
        if (isMappingOrFileError(message) && !message.toLowerCase().includes('queue')) {
          if (isFileScopedError(message) && !message.toLowerCase().includes('mapping')) {
            setFileError(message);
            setCurrentStep(1);
          } else {
            setMappingBanner(message);
            setCurrentStep(2);
          }
          return;
        }
        setImportPhase('failed');
        setUploadError(message);
      }
    },
    [columnMapping, idempotencyKey, selectedFile, selectedListIds, startImport]
  );

  const handleRetryPolling = useCallback(() => {
    setPollError(null);
    void jobQuery.refetch();
  }, [jobQuery]);

  const handleRetry = useCallback(() => {
    // Only for true job failure (status failed) or failed-to-start (no jobId).
    // Poll/transport errors use handleRetryPolling and must not POST again.
    setUploadError(null);
    setPollError(null);
    setImportPhase('progress');
    if (jobId) {
      const nextKey = crypto.randomUUID();
      setJobId(null);
      setIdempotencyKey(nextKey);
      void handleStartImport(nextKey);
      return;
    }
    void handleStartImport();
  }, [handleStartImport, jobId]);

  const handleImportAnother = () => {
    resetFileSelection();
    setColumnMapping({});
    setSelectedListIds([]);
    setPreviewRows([]);
    setPreviewSource(null);
    setPreviewTruncatedAt(null);
    setImportResult(null);
    setImportPhase('progress');
    setJobId(null);
    setIdempotencyKey(crypto.randomUUID());
    setUploadError(null);
    setPollError(null);
    setMappingBanner(null);
    setUnmappedKeys([]);
    setCurrentStep(1);
  };

  useEffect(() => {
    if (importPhase !== 'result' || !jobId || (importResult?.failed ?? 0) === 0) {
      setErrorReport(null);
      return;
    }
    let cancelled = false;
    downloadContactImportErrors(jobId)
      .then((blob) => blob.text())
      .then((text) => {
        if (!cancelled) setErrorReport(parseContactCsvForImport(text));
      })
      .catch(() => {
        if (!cancelled) setErrorReport(null);
      });
    return () => {
      cancelled = true;
    };
  }, [importPhase, jobId, importResult?.failed]);

  const handleDownloadErrors = async () => {
    if (!jobId) return;
    setIsDownloadingErrors(true);
    try {
      const blob = await downloadContactImportErrors(jobId);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `contact-import-errors-${jobId}.csv`;
      anchor.click();
      URL.revokeObjectURL(url);
      setUploadError(null);
    } catch (error) {
      setUploadError(getContactImportErrorMessage(error));
    } finally {
      setIsDownloadingErrors(false);
    }
  };

  const visiblePreviewRows = previewRows.slice(0, PREVIEW_ROW_LIMIT);
  const listOptions = useMemo(
    () =>
      [...lists]
        .filter((list) => list.id && list.name.trim())
        .sort((a, b) => a.name.trim().localeCompare(b.name.trim())),
    [lists]
  );

  return (
    <div className="mx-auto flex h-full min-h-0 w-full max-w-[1000px] flex-col gap-5 overflow-auto p-4 sm:p-6">
      <div className="flex flex-wrap items-start gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-neutral-900">
            Import contacts
          </h1>
          <p className="mt-0.5 text-sm text-neutral-600">
            Bring an existing audience in from a CSV file.
          </p>
        </div>
        <div className="flex-1" />
        <Link
          to="/contacts"
          className="text-xs font-medium text-neutral-600 hover:text-neutral-900"
        >
          Cancel
        </Link>
      </div>

      <ImportContactsStepIndicator currentStep={currentStep} />

      {currentStep === 1 ? (
        <div>
          <div className="flex max-w-[640px] flex-col gap-4 rounded-lg border border-neutral-200 bg-white p-6 shadow-e1">
            <button
              type="button"
              className={cn(
                'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-neutral-300 px-6 py-12 text-center transition-colors',
                isDragOver && 'border-primary-400 bg-primary-50/40',
                !isDragOver && 'hover:border-primary-300 hover:bg-neutral-50/60'
              )}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(event) => {
                event.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={(event) => {
                event.preventDefault();
                setIsDragOver(false);
                const file = event.dataTransfer.files?.[0];
                if (file) void loadFile(file);
              }}
            >
              <CloudArrowUp className="size-8 text-neutral-400" aria-hidden />
              <div className="font-medium text-neutral-900">
                Drag a CSV file here, or click to browse
              </div>
              <p className="text-xs text-neutral-500">CSV files up to 5 MB.</p>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void loadFile(file);
              }}
            />

            {fileError ? (
              <div className="rounded-lg bg-red-50 px-3 py-2.5 text-xs text-red-900">
                {fileError}
              </div>
            ) : null}

            {parsedData && selectedFileName ? (
              <div className="flex items-center gap-3 rounded-lg border border-neutral-200 px-4 py-3">
                <FileText className="size-5 shrink-0 text-primary-600" aria-hidden />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-neutral-900">{selectedFileName}</div>
                  <div className="text-xs text-neutral-500">
                    {parsedData.rows.length.toLocaleString()} rows detected
                    {selectedFileSize
                      ? ` · ${Math.max(1, Math.round(selectedFileSize / 1024)).toLocaleString()} KB`
                      : ''}
                  </div>
                </div>
                <button
                  type="button"
                  className="inline-flex size-8 items-center justify-center rounded-md text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800"
                  aria-label="Remove file"
                  onClick={resetFileSelection}
                >
                  <X className="size-4" aria-hidden />
                </button>
              </div>
            ) : null}

            <button
              type="button"
              className="inline-flex items-center gap-1 self-start text-xs font-medium text-primary-600 hover:text-primary-700"
              onClick={loadSampleFile}
            >
              <Sparkle className="size-3.5" aria-hidden />
              Use a sample file instead
            </button>
          </div>

          <div className="mt-4 flex max-w-[640px] items-center justify-end gap-2">
            <Button
              type="button"
              className="h-10 gap-2 bg-primary-600 px-4 text-white hover:bg-primary-700"
              disabled={!parsedData}
              onClick={() => goToStep(2)}
            >
              Continue
              <ArrowRight className="size-4" aria-hidden />
            </Button>
          </div>
        </div>
      ) : null}

      {currentStep === 2 && parsedData ? (
        <div>
          <div className="flex max-w-[640px] flex-col rounded-lg border border-neutral-200 bg-white p-6 shadow-e1">
            {fieldsError ? (
              <div className="mb-3 rounded-lg bg-red-50 px-3 py-2.5 text-xs text-red-900">
                Could not load import field vocabulary. Mapping options may be incomplete — try
                refreshing the page.
              </div>
            ) : null}
            {mappingBanner ? (
              <div className="mb-3 rounded-lg bg-red-50 px-3 py-2.5 text-xs text-red-900">
                {mappingBanner}
              </div>
            ) : null}

            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">
              Map each CSV column to a contact field
            </div>
            <div className="divide-y divide-neutral-100">
              {parsedData.headers.map((header) => (
                <div key={header} className="flex items-center gap-3 py-2">
                  <div className="w-1/2 min-w-0">
                    <div className="text-xs text-neutral-500">CSV column</div>
                    <div className="truncate font-medium text-neutral-900">{header}</div>
                  </div>
                  <ArrowRight className="size-4 shrink-0 text-neutral-400" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <Select
                      value={columnMapping[header] || SKIP_MAPPING_VALUE}
                      onValueChange={(value) =>
                        setColumnMapping((current) => ({
                          ...current,
                          [header]:
                            value === SKIP_MAPPING_VALUE
                              ? ''
                              : (value as ContactImportMappingTarget),
                        }))
                      }
                    >
                      <SelectTrigger className="h-10 w-full border-neutral-300">
                        <SelectValue placeholder="— Skip this column —" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={SKIP_MAPPING_VALUE}>— Skip this column —</SelectItem>
                        {Object.entries(fieldsByGroup).map(([group, groupFields]) => (
                          <SelectGroup key={group}>
                            <SelectLabel>{FIELD_GROUP_LABELS[group] ?? group}</SelectLabel>
                            {groupFields.map((field) => (
                              <SelectItem key={field.field} value={field.field}>
                                {field.label}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        ))}
                      </SelectContent>
                    </Select>
                    {fieldTypeByTarget.get(columnMapping[header]) === 'multienum' ? (
                      <p className="mt-1 text-xs text-neutral-500">
                        Separate several values with a semicolon, e.g. sports;music
                      </p>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>

            {!mappingValidation.ok ? (
              <div className="mt-3 rounded-lg bg-red-50 px-3 py-2.5 text-xs text-red-900">
                {mappingValidation.error}
              </div>
            ) : null}

            <div className="mt-4 border-t border-neutral-200 pt-4">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">
                Assign to list (optional)
              </div>
              {listOptions.length > 0 ? (
                <div className="flex flex-col gap-2">
                  {listOptions.map((list) => {
                    const checked = selectedListIds.includes(list.id);
                    return (
                      <label
                        key={list.id}
                        className="flex cursor-pointer items-center gap-2 text-sm text-neutral-900"
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(value) => {
                            setSelectedListIds((current) =>
                              value === true
                                ? [...current, list.id]
                                : current.filter((id) => id !== list.id)
                            );
                          }}
                        />
                        {list.name.trim()}
                      </label>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-neutral-500">No lists available yet.</p>
              )}
            </div>
          </div>

          <div className="mt-4 flex max-w-[640px] items-center justify-between gap-2">
            <Button
              type="button"
              variant="ghost"
              className="h-10 gap-2 text-neutral-900"
              onClick={() => goToStep(1)}
            >
              <ArrowLeft className="size-4" aria-hidden />
              Back
            </Button>
            <Button
              type="button"
              className="h-10 gap-2 bg-primary-600 px-4 text-white hover:bg-primary-700"
              disabled={!mappingValidation.ok || fields.length === 0}
              onClick={() => goToStep(3)}
            >
              Continue to preview
              <ArrowRight className="size-4" aria-hidden />
            </Button>
          </div>
        </div>
      ) : null}

      {currentStep === 3 ? (
        <div>
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <KpiTile label="Rows" value={previewCounts.total} />
            <KpiTile label="Valid" value={previewCounts.validCount} />
            <KpiTile label="Invalid" value={previewCounts.invalidCount} />
            <KpiTile label="Will update" value={previewCounts.willUpdateCount} />
          </div>

          <div className="mb-4 flex items-start gap-2.5 rounded-lg bg-primary-50 px-3.5 py-2.5">
            <Info className="mt-0.5 size-4 shrink-0 text-primary-600" aria-hidden />
            <p className="text-xs text-primary-900">
              {previewSource === 'server' && previewCounts.willUpdateCount > 0
                ? `${previewCounts.willUpdateCount.toLocaleString()} row${previewCounts.willUpdateCount === 1 ? '' : 's'} will update existing contacts. `
                : ''}
              Duplicate emails are merged into the existing contact — incoming non-null values
              overwrite the current ones; no duplicate row is created. This is not configurable yet
              (Decision: Contact Deduplication, overwrite-only for MVP).
            </p>
          </div>

          {isPreviewing ? (
            <p className="mb-3 flex items-center gap-2 text-xs text-neutral-600">
              <SpinnerGap className="size-3.5 animate-spin" aria-hidden />
              Checking rows against the import rules and existing contacts…
            </p>
          ) : null}
          {previewSource === 'local' ? (
            <p className="mb-3 text-xs text-amber-800">
              Could not run the full check, so only email format and required columns were checked
              here. Duplicates are not shown.
            </p>
          ) : null}
          {previewTruncatedAt ? (
            <p className="mb-3 text-xs text-neutral-600">
              The full check covered the first {previewTruncatedAt.toLocaleString()} rows; the rest
              were checked locally.
            </p>
          ) : null}

          <div className="flex flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-e1">
            <div className="max-h-[360px] overflow-auto">
              <Table className="min-w-[640px]">
                <TableHeader>
                  <TableRow className="border-b border-neutral-200 bg-neutral-50 hover:bg-neutral-50">
                    <TableHead className="sticky top-0 z-10 bg-neutral-50 px-4 py-2.5 text-xs font-semibold text-neutral-600">
                      Row
                    </TableHead>
                    <TableHead className="sticky top-0 z-10 bg-neutral-50 px-4 py-2.5 text-xs font-semibold text-neutral-600">
                      Email
                    </TableHead>
                    <TableHead className="sticky top-0 z-10 bg-neutral-50 px-4 py-2.5 text-xs font-semibold text-neutral-600">
                      Phone
                    </TableHead>
                    <TableHead className="sticky top-0 z-10 bg-neutral-50 px-4 py-2.5 text-xs font-semibold text-neutral-600">
                      Name
                    </TableHead>
                    <TableHead className="sticky top-0 z-10 bg-neutral-50 px-4 py-2.5 text-xs font-semibold text-neutral-600">
                      Status
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visiblePreviewRows.map((row) => {
                    const name = `${row.firstName} ${row.lastName}`.trim() || '—';
                    return (
                      <TableRow
                        key={row.index}
                        className="border-b border-neutral-100 hover:bg-transparent"
                      >
                        <TableCell className="px-4 py-2.5 text-neutral-500">{row.index}</TableCell>
                        <TableCell className="px-4 py-2.5">{row.email || '—'}</TableCell>
                        <TableCell className="px-4 py-2.5 text-neutral-600">
                          {row.phone || '—'}
                        </TableCell>
                        <TableCell className="px-4 py-2.5 text-neutral-600">{name}</TableCell>
                        <TableCell className="px-4 py-2.5">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <ImportContactsStatusBadge status={row.status} />
                            {row.willUpdate ? (
                              <span
                                className="inline-flex items-center rounded-full bg-primary-50 px-2.5 py-0.5 text-xs font-medium whitespace-nowrap text-primary-700"
                                title={
                                  row.duplicateOfRow
                                    ? `Same email or phone as row ${row.duplicateOfRow}`
                                    : 'Matches an existing contact'
                                }
                              >
                                Duplicate · updates
                              </span>
                            ) : null}
                          </div>
                          {row.reasons.length > 0 ? (
                            <div className="mt-0.5 text-xs text-neutral-500">
                              {row.reasons.join('; ')}
                            </div>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>

          <p className="mt-2 text-xs text-neutral-500">
            {previewRows.length > PREVIEW_ROW_LIMIT
              ? `Showing first ${PREVIEW_ROW_LIMIT} of ${previewRows.length.toLocaleString()} rows.`
              : `Showing all ${previewRows.length.toLocaleString()} row${previewRows.length === 1 ? '' : 's'}.`}
          </p>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <Button
              type="button"
              variant="ghost"
              className="h-10 gap-2 text-neutral-900"
              onClick={() => goToStep(2)}
            >
              <ArrowLeft className="size-4" aria-hidden />
              Back
            </Button>
            <Button
              type="button"
              className="h-10 gap-2 bg-primary-600 px-4 text-white hover:bg-primary-700"
              disabled={previewCounts.importableCount === 0 || isStartingImport || isPreviewing}
              onClick={() => void handleStartImport()}
            >
              Start Import
              <ArrowRight className="size-4" aria-hidden />
            </Button>
          </div>
        </div>
      ) : null}

      {currentStep === 4 ? (
        <div>
          {importPhase === 'progress' ? (
            <div className="mx-auto flex max-w-[480px] flex-col items-center gap-3 rounded-lg border border-neutral-200 bg-white p-8 text-center shadow-e1">
              <SpinnerGap className="size-7 animate-spin text-primary-600" aria-hidden />
              <div className="text-lg font-semibold text-neutral-900">
                {jobStatus === 'queued' || (!jobId && isStartingImport)
                  ? 'Waiting for worker…'
                  : 'Importing your contacts…'}
              </div>
              <p className="text-sm text-neutral-600">
                {jobStatus === 'queued'
                  ? 'Your file is queued. Progress will start once a worker picks it up.'
                  : 'This can take a few minutes for large files. You can leave this page — the import keeps running.'}
              </p>
              <ImportJobStages status={jobStatus || 'queued'} />
              <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-neutral-100">
                <div
                  className="h-full rounded-full bg-primary-600 transition-[width] duration-500 ease-out"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              {jobStatus === 'processing' && jobQuery.data?.totals?.rows ? (
                <p className="text-xs text-neutral-600">
                  {processedRows.toLocaleString()} of {jobQuery.data.totals.rows.toLocaleString()}{' '}
                  rows processed
                </p>
              ) : null}
              {pollError ? (
                <div className="mt-2 w-full rounded-lg bg-amber-50 px-3 py-2.5 text-left text-xs text-amber-950">
                  <p className="mb-2">
                    Could not refresh import status: {pollError}. The import may still be running.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-8 gap-1.5 px-3 text-xs"
                    onClick={handleRetryPolling}
                  >
                    <ArrowClockwise className="size-3.5" aria-hidden />
                    Retry polling
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}

          {importPhase === 'failed' ? (
            <div className="mx-auto flex max-w-[480px] flex-col items-center gap-3 rounded-lg border border-neutral-200 bg-white p-8 text-center shadow-e1">
              <div className="grid size-12 place-items-center rounded-full bg-red-50">
                <WarningCircle className="size-6 text-red-600" aria-hidden />
              </div>
              <div className="text-lg font-semibold text-neutral-900">
                {jobId ? 'Import failed' : 'Import failed to start'}
              </div>
              <p className="text-sm text-neutral-600">
                {uploadError ||
                  (jobId
                    ? 'Something went wrong while processing this import. You can retry.'
                    : 'Something went wrong queuing this import. Your file was not processed — try again.')}
              </p>
              <Button
                type="button"
                className="mt-1 h-10 gap-2 bg-primary-600 px-4 text-white hover:bg-primary-700"
                disabled={isStartingImport}
                onClick={handleRetry}
              >
                <ArrowClockwise className="size-4" aria-hidden />
                Retry
              </Button>
            </div>
          ) : null}

          {importPhase === 'result' && importResult ? (
            <div className="mx-auto flex max-w-[560px] flex-col items-center gap-4 rounded-lg border border-neutral-200 bg-white p-8 text-center shadow-e1">
              {importResult.allRowsFailed ? (
                <>
                  <div className="grid size-12 place-items-center rounded-full bg-red-50">
                    <WarningCircle className="size-6 text-red-600" aria-hidden />
                  </div>
                  <div className="text-lg font-semibold text-neutral-900">No contacts imported</div>
                  <p className="text-sm text-neutral-600">
                    Every row in this file failed. Download the error report to see why.
                  </p>
                </>
              ) : (
                <>
                  <div className="grid size-12 place-items-center rounded-full bg-emerald-50">
                    <CheckCircle className="size-6 text-emerald-700" aria-hidden />
                  </div>
                  <div className="text-lg font-semibold text-neutral-900">Import complete</div>
                </>
              )}
              <div className="grid w-full grid-cols-2 gap-3 sm:grid-cols-5">
                <KpiTile label="Rows" value={importResult.rows} />
                <KpiTile label="Created" value={importResult.created} />
                <KpiTile label="Updated" value={importResult.updated} />
                <KpiTile label="Failed" value={importResult.failed} />
                <KpiTile label="Needs confirmation" value={importResult.needsConfirmation} />
              </div>

              {uploadError ? (
                <div className="w-full rounded-lg bg-red-50 px-3 py-2.5 text-left text-xs text-red-900">
                  {uploadError}
                </div>
              ) : null}

              {unmappedKeys.length > 0 ? (
                <div className="w-full text-left">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Unmapped keys
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {unmappedKeys.map((key) => (
                      <span
                        key={key}
                        className="inline-flex items-center rounded-md bg-neutral-100 px-2 py-0.5 text-xs text-neutral-700"
                      >
                        {key}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}

              {errorReport && errorReport.rows.length > 0 ? (
                <div className="max-h-60 w-full overflow-auto rounded-lg border border-neutral-200 text-left">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-neutral-50 hover:bg-neutral-50">
                        {errorReport.headers.map((header) => (
                          <TableHead
                            key={header}
                            className="sticky top-0 bg-neutral-50 px-3 py-2 text-xs font-semibold text-neutral-600"
                          >
                            {header}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {errorReport.rows.slice(0, 50).map((row, rowIndex) => (
                        <TableRow key={rowIndex} className="hover:bg-transparent">
                          {errorReport.headers.map((header) => (
                            <TableCell key={header} className="px-3 py-2 text-xs">
                              {row[header] || '—'}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : null}

              {importResult.failed > 0 ? (
                <Button
                  type="button"
                  variant="outline"
                  className="h-10 gap-2"
                  disabled={isDownloadingErrors}
                  onClick={() => void handleDownloadErrors()}
                >
                  <DownloadSimple className="size-4" aria-hidden />
                  Download error report
                </Button>
              ) : null}

              <div className="mt-1 flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  className="h-10"
                  onClick={handleImportAnother}
                >
                  Import another file
                </Button>
                <Button
                  type="button"
                  className="h-10 bg-primary-600 px-4 text-white hover:bg-primary-700"
                  onClick={() => navigate('/contacts')}
                >
                  View contacts
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
